import React, { useMemo, useCallback, useState, useEffect } from "react";
import {
  Panel,
  PanelType as FluentPanelType,
  Text,
  Stack,
} from "@fluentui/react";
import { useGetPolicyConstraintsQuery } from "services/template";
import usePolicyStructureEditor from "hooks/policy/usePolicyStructureEditor";
import { hasReadOnlyAccess } from "components/AppContent";
import { usePolicyEditStructureNodeManager } from "../structure/PolicyEditStructureNodeManager";
import PolicyCriteriaAdder from "./PolicyCriteriaAdder";
import PolicyCriteriaEditPanelActions from "./PolicyCriteriaEditPanelActions";
import PolicyCriteriaEditList from "./PolicyCriteriaEditList";

interface PolicyCriteriaEditPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
}

function PolicyCriteriaEditPanel({
  isOpen,
  onDismiss,
}: PolicyCriteriaEditPanelProps) {
  const { selectedTemplates } = usePolicyEditStructureNodeManager();
  const { data: allConstraints = [], isLoading } = useGetPolicyConstraintsQuery();
  const { updateTemplateCriteria } = usePolicyStructureEditor(onDismiss);
  const isReadOnly = hasReadOnlyAccess();

  // Get the first selected template for criteria editing
  const selectedTemplate = selectedTemplates[0];

  // State for managing criteria like template constraints
  const [criteriaEdit, setCriteriaEdit] = useState<any[]>([]);
  const [usedCriteria, setUsedCriteria] = useState<string[]>([]);
  const [isApplying, setIsApplying] = useState(false);

  // Initialize criteria from template - enrich with correct keyId from allConstraints
  // Only initialize once allConstraints have loaded to avoid double-render
  const hasInitialized = React.useRef(false);
  useEffect(() => {
    if (hasInitialized.current) return;

    if (selectedTemplate?.constraints && allConstraints.length > 0) {
      hasInitialized.current = true;
      // Enrich constraints with correct keyId from allConstraints API
      const enrichedConstraints = selectedTemplate.constraints.map((c: any) => {
        // Find matching constraint from API to get correct keyId (UUID)
        const matchingConstraint = allConstraints.find(
          (ac: any) => ac.name === c.name || ac.name === c.keyId || ac.key === c.keyId || ac.id === c.keyId
        );
        if (matchingConstraint) {
          return {
            ...c,
            keyId: matchingConstraint.id || matchingConstraint.key,
            keyDisplay: matchingConstraint.display || matchingConstraint.name || c.keyDisplay || c.name,
          };
        }
        return c;
      });
      setCriteriaEdit(enrichedConstraints);
      setUsedCriteria(enrichedConstraints.map((c: any) => c.name));
    }
  }, [selectedTemplate, allConstraints]);

  // Filter out constraints that are already applied
  const availableConstraints = useMemo(() => {
    if (!allConstraints.length) return [];
    return allConstraints;
  }, [allConstraints]);

  const hasSelectedTemplate = !!selectedTemplate;

  // Add constraint function like template edit
  const addConstraints = useCallback(
    (option?: any) => {
      const selected = option?.selected;
      setCriteriaEdit((previousCriteria) => {
        const clonedCriteria = [...previousCriteria];
        const currInd = clonedCriteria.findIndex(
          (c) => c.name === option.key
        );
        if (!selected) {
          clonedCriteria.splice(currInd, 1);
        } else {
          const selectedConstraint = allConstraints.find(
            (c) => c.name === option.key || c.key === option.key || c.id === option.key
          );
          clonedCriteria.push({
            id: `${selectedTemplate.id}-${selectedConstraint?.id || selectedConstraint?.key}`,
            name: selectedConstraint?.name || option.key,
            value: "",
            operator: "Include",
            key: option.key,
            keyId: selectedConstraint?.id || selectedConstraint?.key,
            keyDisplay: option.text,
            valueDisplay: "",
          });
        }
        return clonedCriteria;
      });
    },
    [setCriteriaEdit, allConstraints, selectedTemplate]
  );

  // Update constraint function like template edit
  const onCriteriaUpdated = useCallback(
    (criteria: any, criteriaIndex: number, isRemoved?: boolean) => {
      setCriteriaEdit((previousCriteria) => {
        const clonedCriteria = [...previousCriteria];
        
        if (isRemoved) {
          clonedCriteria.splice(criteriaIndex, 1);
          return clonedCriteria;
        }
        
        clonedCriteria[criteriaIndex] = criteria;
        return clonedCriteria;
      });
    },
    []
  );

  // Update used criteria when criteria edit changes
  useEffect(() => {
    if (criteriaEdit) {
      setUsedCriteria(criteriaEdit.map((c) => c.key || c.name));
    }
  }, [criteriaEdit]);

  // Handle Apply button click - save criteria to template
  const handleApply = useCallback(async () => {
    if (!selectedTemplate) {
      return;
    }

    setIsApplying(true);
    try {
      // Save the criteria to the template using the policy structure editor
      // Allow empty criteriaEdit array to remove all criteria
      const result = await updateTemplateCriteria(selectedTemplate.id, criteriaEdit || []);
      
      if (result.success) {
        // Successfully saved - the hook will handle closing the panel
      } else {
        // Handle error - in a real implementation, this could show a toast notification
      }
    } catch (error) {
      // Handle error case
    } finally {
      setIsApplying(false);
    }
  }, [selectedTemplate, criteriaEdit, updateTemplateCriteria]);

  // Memoized render functions to avoid ESLint warnings
  const renderFooter = useCallback(() => {
    // Check if we're editing existing criteria (template originally had constraints)
    // const isEditingExisting = selectedTemplate?.constraints && selectedTemplate.constraints.length > 0;
    
    // Check if Apply button should be disabled
    const isDisabled = !criteriaEdit?.length || 
      (criteriaEdit?.length > 0 && !criteriaEdit?.every((c) => {
        // Check if constraint has values (single or multiple)
        const hasValue = c.value && c.value !== "" || 
                        (c.values && Array.isArray(c.values) && c.values.length > 0);
        return hasValue;
      }));

    return (
      <PolicyCriteriaEditPanelActions
        onApply={handleApply}
        disabled={isDisabled || isReadOnly}
        isApplying={isApplying}
      />
    );
  }, [handleApply, criteriaEdit, selectedTemplate, isApplying]);

  return (
    <Panel
      data-automation-id="policyEdit-structure-editCriteria-panel"
      headerText="Add criteria"
      type={FluentPanelType.custom}
      customWidth="500px"
      isOpen={isOpen}
      onDismiss={onDismiss}
      isLightDismiss={false}
      onRenderFooter={renderFooter}
      isFooterAtBottom
      styles={{
        main: {
          zIndex: 1000,
        },
        overlay: {
          zIndex: 999,
        },
        footerInner: {
          padding: 0,
        },
        content: {
          padding: '0 24px',
        },
        scrollableContent: {
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      <div style={{ padding: "20px 0" }}>
        {!hasSelectedTemplate ? (
          <Stack tokens={{ childrenGap: 16 }}>
            <Text variant="medium">
              Please select a template to edit its criteria.
            </Text>
          </Stack>
        ) : (
          <div>
            <Text variant="small" style={{ marginBottom: "16px", display: "block", color: "#666" }}>
              The policy will be applicable to the users within the template when the criteria defined below is met.
            </Text>
            {isLoading ? (
              <div style={{ marginBottom: "16px" }}>
                <Text variant="small">Loading available criteria...</Text>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: "16px" }}>
                  <PolicyCriteriaAdder 
                    usedConstraints={usedCriteria}
                    availableConstraints={availableConstraints}
                    onConstraintSelected={addConstraints}
                    disabled={isReadOnly}
                  />
                </div>
                {criteriaEdit && criteriaEdit.length > 0 && (
                  <PolicyCriteriaEditList
                    constraints={criteriaEdit}

                    onConstraintUpdated={onCriteriaUpdated}
                    disabled={isReadOnly}
                  />
                )}
              </>
            )}
          </div>
        )}
      </div>
    </Panel>
  );
}

export default PolicyCriteriaEditPanel;